import { type ReactElement } from 'react';
import {
  DATA_SLOT,
  NEON_ADD_DOCUMENT_ILLUSTRATION,
  NEON_GRID_CONTAINER_CLASS,
  NEON_TEXT,
  NeonButtonTypes,
  NeonGridColumn,
  NeonIllustrationBackground,
} from '@/constants/neon-constants';
import { NeonButton, NeonCardStandard } from '@ps-refarch-ux/neon';
import {
  EMPTY_STATE_BODY_STYLE,
  EMPTY_STATE_CARD_STYLE,
  EMPTY_STATE_ILLUSTRATION_STYLE,
  EMPTY_STATE_TITLE_STYLE,
} from './empty-state.styles';
import { EMPTY_SUBJECT_AREA_ID, EMPTY_SUBJECT_AREA_TEXT } from '@/constants/subject-area-constants';
import { NeonIllustrations } from '@/components/NeonIllustration/neon-illustration';

type EmptyStateProps = {
  title?: string;
  body?: string;
  buttonText?: string;
  buttonId?: string;
  /** Icon for the action button (e.g. "add"). Pass false or empty string to show no icon (e.g. for "Clear filters"). */
  buttonIcon?: string | false;
  actionAriaHaspopup?: 'true' | 'false' | 'dialog' | 'menu' | 'grid' | 'listbox' | 'tree';
  onAction?: () => void;
};

export const EmptyState = ({
  title = EMPTY_SUBJECT_AREA_TEXT.Title,
  body,
  buttonText = EMPTY_SUBJECT_AREA_TEXT.Button,
  buttonId,
  buttonIcon = 'add',
  actionAriaHaspopup,
  onAction,
}: EmptyStateProps): ReactElement => {
  const icon = buttonIcon === false || buttonIcon === '' ? undefined : buttonIcon;
  const resolvedBody = body !== undefined ? body : EMPTY_SUBJECT_AREA_TEXT.Body;
  const showBody = resolvedBody.trim().length > 0;

  return (
    <NeonCardStandard>
      <div data-slot={DATA_SLOT} style={EMPTY_STATE_CARD_STYLE}>
        <NeonIllustrations
          dataBackground={NeonIllustrationBackground.Complex}
          dataIllustration={NEON_ADD_DOCUMENT_ILLUSTRATION.AddDocument}
          containerClassName={NEON_GRID_CONTAINER_CLASS.GridContainer}
          columnClassName={`${NeonGridColumn.Two} ${NeonGridColumn.MediumFour} ${NeonGridColumn.LargeFour}`}
          illustrationStyle={EMPTY_STATE_ILLUSTRATION_STYLE}
        />
        <p className={NEON_TEXT} style={EMPTY_STATE_TITLE_STYLE}>
          {title}
        </p>
        {showBody && (
          <p className={NEON_TEXT} style={EMPTY_STATE_BODY_STYLE}>
            {resolvedBody}
          </p>
        )}
        {onAction !== undefined && (
          <NeonButton
            id={buttonId ?? EMPTY_SUBJECT_AREA_ID.Button}
            dataType={NeonButtonTypes.Primary}
            dataText={buttonText}
            {...(icon !== undefined && { dataIcon: icon })}
            {...(actionAriaHaspopup !== undefined ? { dataAriaHaspopup: actionAriaHaspopup } : {})}
            onClick={onAction}
          />
        )}
      </div>
    </NeonCardStandard>
  );
};
